import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import type { LocationData } from '@/shared/api/location'
import { getTimeSlots } from '@/shared/api/timeSlots'
import { useLocationRef } from '@/contexts/RefContext'
import { useAppDispatch } from '@/store'
import { setOrderType, setTable, setCurrentRef, clearStoreSettingsError } from '@/store/ticketSlice'
import { PATHS } from '@/router/paths'
import { QUERY_STALE_TIMES } from '@/lib/queryConfig'

const MAX_TABLE_NAME_LENGTH = 64
const TABLE_NAME_INJECTION_CHARS = /[<>"'`]/g

interface StartOrderProps {
	location: LocationData
	isStoreClosureActive: boolean
}

export function StartOrder({ location, isStoreClosureActive }: StartOrderProps) {
	const ref = useLocationRef()
	const [searchParams] = useSearchParams()
	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	const hasNavigated = useRef(false)

	// Support Ember QR codes (?dineIn=true&tableNotes=...) and new format (?tableName=...)
	// tableName alone implies dine-in — matches Ember's QR code contract (intentional)
	const rawTable = searchParams.get('tableName') ?? searchParams.get('tableNotes')
	// Sanitize: strip injection chars, cap length before storing in Redux/localStorage (OWASP A03)
	const tableName = rawTable
		? rawTable.replace(TABLE_NAME_INJECTION_CHARS, '').trim().slice(0, MAX_TABLE_NAME_LENGTH) ||
			null
		: null
	const isDineInScan = !!tableName || searchParams.get('dineIn') === 'true'
	const dineInOrderType = location.orderTypes.find((ot) => ot.ref === 'DINE_IN') ?? null
	const firstOrderType = location.orderTypes[0] ?? null
	const takeoutOrderType = location.orderTypes.find((ot) => ot.ref === 'TAKEOUT') ?? null
	const hasTakeout = takeoutOrderType !== null

	// Fetch time-slots for PATH 1 ASAP auto-skip — skip when dine-in scan (PATH 3 handles it)
	// or when TAKEOUT is not available at this location
	const shouldFetchTimeSlots = hasTakeout && !isDineInScan

	const { data: timeSlotsData, isError: isTimeSlotsError } = useQuery({
		queryKey: ['timeSlots', ref, takeoutOrderType?.ref],
		queryFn: () => getTimeSlots(ref, takeoutOrderType!.ref),
		enabled: shouldFetchTimeSlots,
		staleTime: QUERY_STALE_TIMES.SHORT,
	})

	useEffect(() => {
		if (hasNavigated.current) return

		// PATH 3 — QR-code dine-in scan: dineIn flag or table name present
		// Prefer DINE_IN order type; fall back to first available if API didn't return it
		if (isDineInScan) {
			const orderTypeForDineIn = dineInOrderType ?? firstOrderType
			if (orderTypeForDineIn) {
				dispatch(clearStoreSettingsError())
				dispatch(setCurrentRef(ref))
				dispatch(
					setOrderType({ guid: orderTypeForDineIn.ref, orderType: orderTypeForDineIn }),
				)
				if (tableName) dispatch(setTable(tableName))
				// Guard set before the isStoreClosureActive check intentionally — prevents
				// repeated Redux dispatches on every re-render while a closure is active.
				// Moving it inside the navigation block would allow re-dispatch on each render
				// until the closure clears. A closure lifting while a dine-in user waits on
				// this page is not a real use case — the guard placement is correct as-is.
				hasNavigated.current = true
				if (!isStoreClosureActive) {
					void navigate(PATHS.menu(ref), { replace: true })
				}
				return
			}
			// No order type found — fall through to PATH 2
		}

		// Suppress PATH 1 and PATH 2 navigation when store is dynamically closed
		if (isStoreClosureActive) return

		// Fail-safe to PATH 2 on API error — prevents blank screen when time-slots fetch fails
		if (shouldFetchTimeSlots && isTimeSlotsError) {
			hasNavigated.current = true
			void navigate(PATHS.orderSettings(ref), { replace: true })
			return
		}

		// PATH 1 conditions met but time-slots not yet loaded — wait
		if (shouldFetchTimeSlots && !timeSlotsData) return

		// PATH 1 — ASAP auto-skip: TAKEOUT exists + ASAP available
		// Future slots existing does not block the skip — PO requirement 2026-06-23
		if (timeSlotsData && takeoutOrderType) {
			const asapOk = timeSlotsData.asapTimeSlot?.isAvailable === true
			if (asapOk) {
				dispatch(clearStoreSettingsError())
				dispatch(setCurrentRef(ref))
				dispatch(setOrderType({ guid: takeoutOrderType.ref, orderType: takeoutOrderType }))
				hasNavigated.current = true
				void navigate(PATHS.menu(ref), { replace: true })
				return
			}
		}

		// PATH 2 — fallback: show the order-settings modal
		hasNavigated.current = true
		void navigate(PATHS.orderSettings(ref), { replace: true })
	}, [
		isDineInScan,
		tableName,
		dineInOrderType,
		firstOrderType,
		isStoreClosureActive,
		shouldFetchTimeSlots,
		timeSlotsData,
		isTimeSlotsError,
		hasTakeout,
		takeoutOrderType,
		ref,
		dispatch,
		navigate,
	])

	return null
}
